import { Modal } from '@zendeskgarden/react-modals'
import React, { useMemo, useState } from 'react'
import { DragDropContext, DropResult, Droppable } from 'react-beautiful-dnd'

import CloseIcon from '../../icons/ps_reporting_close.svg?react'
import { Option } from '../../models/Option'
import { PSComponent } from '../../models/PSComponent'
import { RecordFactory } from '../../models/RecordFactory'
import { DangerModal } from '../common/modals/DangerModal'
import { ReportMetricsCard } from './ReportMetricsCard'
import { ReportSelectMetricModal } from './ReportSelectMetricModal'

type ReportMetricsModalProps = {
    metrics: PSComponent[]
    onClose: (metrics: PSComponent[]) => void
}

export const ReportMetricsModal: React.FC<ReportMetricsModalProps> = ({ metrics, onClose }) => {
    const [editMetricModal, setEditMetricModal] = useState<React.ReactNode | undefined>(undefined)
    const [deleteGuardrail, setDeleteGuardrail] = useState<React.ReactNode | undefined>(undefined)
    const [metricData, setMetricData] = useState<PSComponent[]>(metrics)

    function onEdit(metric: PSComponent) {
        const editingIndex = metricData.findIndex((m) => m.id === metric.id)
        const onSelect = async (option: Option) => {
            const metric = (await RecordFactory.getInstance().loadComponent(option.id))!
            setMetricData((prev) => {
                const newMetricData = [...prev] // create a copy of the state
                if (editingIndex !== -1) {
                    newMetricData[editingIndex] = metric
                }
                else {
                    newMetricData.push(metric)
                }
                return newMetricData
            })
            setEditMetricModal(undefined)
        }
        const onClose = () => {
            setEditMetricModal(undefined)
        }
        setEditMetricModal(
            <ReportSelectMetricModal
                selected={metric}
                metricData={metricData}
                onSelect={onSelect}
                onClose={onClose}
            />
        )
    }

    function onDelete(metric: PSComponent) {
        setDeleteGuardrail(
            <DangerModal
                title={`Delete ${metric.title}`}
                body={'This action cannot be undone'}
                buttonText={'Yes, delete'}
                dismissButtonText={'Cancel'}
                onClose={() => setDeleteGuardrail(undefined)}
                onAction={() => {
                    setMetricData((prev) => prev.filter((m) => m.id !== metric.id))
                    setDeleteGuardrail(undefined)
                }}
            />
        )
    }

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) return

        const reorderedMetrics = Array.from(metricData)
        const [removed] = reorderedMetrics.splice(result.source.index, 1)
        reorderedMetrics.splice(result.destination.index, 0, removed)

        setMetricData(reorderedMetrics)
    }

    return (
        <Modal
            aria-label="Metrics modal"
            className="reportMetricModal"
            onClose={() => onClose(metricData)}
        >
            <div className="header">
                <div className="left">
                    <div className="title">Metrics</div>
                    <div className="count">{`(${metricData.length}/4)`}</div>
                </div>
                <div className="right">
                    <button className="close" tabIndex={0} onClick={() => onClose(metricData)}>
                        <CloseIcon color="#314A68" />
                    </button>
                </div>
            </div>
            <div className="body">
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="metrics">
                        {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                                {metricData.map((metric, index) => (
                                    <ReportMetricsCard
                                        key={metric.id}
                                        metric={metric}
                                        index={index}
                                        onDelete={onDelete}
                                        onEdit={onEdit}
                                        hasOneCard={metricData.length <= 1}
                                    />
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
            {deleteGuardrail}
            {editMetricModal}
        </Modal>
    )
}
